import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { ProjectDetails, ProjectDetailsDTO } from "../types/projectDetails";
import { SurveyDetails, SurveyDetailsDTO } from "./surveyDetails";
/* eslint-disable import/export */
export abstract class GetProjectSurveyDetailsRPC implements
  LeoRPC<GetProjectSurveyDetailsRPC.Request, GetProjectSurveyDetailsRPC.Response, GetProjectSurveyDetailsRPC.Errors.Errors>
{
  abstract execute(
    request: GetProjectSurveyDetailsRPC.Request
  ): Promise<LeoRPCResult<GetProjectSurveyDetailsRPC.Response, GetProjectSurveyDetailsRPC.Errors.Errors>>;
}

export namespace GetProjectSurveyDetailsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetProjectSurveyDetailsRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
  
  
    constructor(
    projectId: LeoUUID
    ) {
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetProjectSurveyDetailsRPC(projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    projectDetails: ProjectDetailsDTO;
    surveyDetails: SurveyDetailsDTO[];
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetProjectSurveyDetailsResponse: boolean = true;
  
    readonly projectDetails: ProjectDetails;
    readonly surveyDetails: SurveyDetails[];
  
  
    constructor(
    projectDetails: ProjectDetails, 
    surveyDetails: SurveyDetails[]
    ) {
      this.projectDetails = projectDetails;
      this.surveyDetails = surveyDetails;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const projectDetails = ProjectDetails.fromDTO(getObject(dto, "projectDetails", InvalidResponseProjectDetailsError));
      const surveyDetails = getList<SurveyDetailsDTO, SurveyDetails>(
        dto,
        "surveyDetails",
        SurveyDetails.fromDTO,
        InvalidResponseSurveyDetailsError
      );
      return new Response(
        projectDetails,
        surveyDetails
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          projectDetails: this.projectDetails.toDTO(),
          surveyDetails: this.surveyDetails.map((e: SurveyDetails) => { return e.toDTO() })
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetProjectSurveyDetails(projectDetails='${this.projectDetails}',surveyDetails='${this.surveyDetails}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetProjectSurveyDetailsRPC.INVALID_PROJECT_ID()`;
    };
    

    export type Errors = InvalidProjectId;
    export type ErrorsDTO = InvalidProjectIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetProjectSurveyDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseProjectDetailsError extends GetProjectSurveyDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectDetailsError";
  }
}

export class InvalidResponseSurveyDetailsError extends GetProjectSurveyDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyDetailsError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetProjectSurveyDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

